'use client';

import { FC, useEffect, useState } from 'react';

import { Message, MessageContent } from '@/components/common';
import { SuggestionItemGroup } from '@/components/features/Editor';
import {
  PromptEditorSheet,
  suggestTitle,
} from '@/components/features/PromptEditor';
import { MessageActions } from '@/components/ui/message-actions-clean';
import { cn } from '@/lib/utils';
import { usePromptsEnabled } from '@/store/prompts';
import { User } from '@/types/auth';
import { ChatMessage } from '@/types/chat';
import { PROMPT_AGENT_IDS, PromptAgent } from '@/types/prompts';

interface Props {
  user: User | null;
  message: ChatMessage;
  possibleCommands?: SuggestionItemGroup[];
  /** The chat's agent (Connie, Tia or Salma): Save to prompts targets it. */
  agentName?: string;
}

/** How long "Saved to My prompts" stays under the bubble. */
const SAVED_NOTE_MS = 2000;

const promptAgentId = (agentName: string | undefined) => {
  const key = agentName?.trim().toLowerCase();
  return key && key in PROMPT_AGENT_IDS
    ? PROMPT_AGENT_IDS[key as PromptAgent]
    : undefined;
};

const UserMessage: FC<Props> = ({
  user,
  message,
  possibleCommands,
  agentName,
}) => {
  // Save to prompts (VH3_CONNECT #594): with the tenant switch on, in a chat
  // with an agent that takes prompts.
  const promptsEnabled = usePromptsEnabled();
  const agentId = promptAgentId(agentName);
  const canSave = promptsEnabled && agentId !== undefined;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [hasOpenedSheet, setHasOpenedSheet] = useState(false);
  const [savedNote, setSavedNote] = useState(0);

  useEffect(() => {
    if (!savedNote) return;
    const timer = window.setTimeout(() => setSavedNote(0), SAVED_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [savedNote]);

  if (!user) {
    return null;
  }

  const openSheet = () => {
    setHasOpenedSheet(true);
    setIsSheetOpen(true);
  };

  return (
    <Message key={message.id} className={cn('justify-end')}>
      {/* data-ui-message lights the quiet action bar while the bubble is hovered. */}
      <div className='relative flex flex-col group/message' data-ui-message>
        <MessageContent
          className={cn(
            'user-message-bubble chat-copy rounded-xl p-4 pt-3',
            // The same surface and hairline as the working panel and the
            // citation preview, so every box in the drawer matches.
            'bg-[var(--chakra-colors-cards-bg)] text-fg-default border border-[var(--chakra-colors-border-light)]'
          )}
          possibleCommands={possibleCommands}
          prompts={message.prompts}
        >
          {message.content}
        </MessageContent>

        {/* affectUI Message Actions with Copy and, with Prompt Templates on,
            Save to prompts: no regenerate, thumbs or read aloud on the
            user's own words. */}
        <div
          className='user-message-actions flex w-full items-center gap-1 mt-0.5 px-2'
          data-cy='user-message-actions'
        >
          <MessageActions
            text={message.content}
            actions={canSave ? ['copy', 'save'] : ['copy']}
            onSave={openSheet}
            label='Your message actions'
            className='user-message-actions__bar'
          />
          {savedNote > 0 && (
            <span
              key={savedNote}
              className='user-message-actions__note text-xs text-fg-muted whitespace-nowrap'
              role='status'
              data-cy='user-message-saved-note'
            >
              Saved to My prompts
            </span>
          )}
          <span className='text-xs text-fg-muted whitespace-nowrap'>
            {message.timestamp}
          </span>
        </div>
      </div>

      {canSave && hasOpenedSheet && (
        <PromptEditorSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          heading='Save to prompts'
          initial={{
            title: suggestTitle(message.content),
            body: message.content,
            agentIds: [agentId],
            groupId: 0,
          }}
          onSaved={() => setSavedNote(Date.now())}
        />
      )}

      {/* <MessageAvatar
        src={user.profile_picture?.url || ''}
        alt='User'
        fallback={
          user?.first_name?.[0]?.toUpperCase() ||
          user?.email?.[0]?.toUpperCase() ||
          '?'
        }
      /> */}
    </Message>
  );
};

export { UserMessage };
